(function () {
  // Single source of truth for wishlist handles
  const STORAGE_KEY = 'sm_wishlist_handles_v1';

  // ---------------------------
  // Helpers: localStorage
  // ---------------------------
  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }

  // ---------------------------
  // Badge in header (wishlist icon)
  // ---------------------------
  function updateWishlistBadge() {
    const badge = document.querySelector('.sm-wishlist-count-badge');
    if (!badge) return;

    const count = getWishlist().length;

    if (count > 0) {
      badge.textContent = count;
      badge.style.display = 'inline-flex';
    } else {
      badge.textContent = '';
      badge.style.display = 'none';
    }
  }

  // Show / hide the wishlist header buttons on the wishlist page
  function setActionsVisibility(hasItems) {
    const actions = document.querySelector('.sm-wishlist-buttons');
    if (!actions) return;

    actions.classList.toggle('sm-wishlist-buttons--hidden', !hasItems);
  }

  function toggleWishlist(handle) {
    let list = getWishlist();
    if (list.includes(handle)) {
      list = list.filter(h => h !== handle);
    } else {
      list.push(handle);
    }
    saveWishlist(list);
    updateWishlistBadge(); // <-- update header badge whenever list changes
    return list;
  }

  function updateButtonState(btn, active) {
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  }

  // ---------------------------
  // Hearts (collections + PDP + wishlist page)
  // ---------------------------
  function initButtons() {
    const buttons = document.querySelectorAll('.sm-wishlist-btn');
    if (!buttons.length) return;

    const list = getWishlist();

    buttons.forEach(btn => {
      // avoid binding multiple listeners to the same button
      if (btn.dataset.smWishlistInit === 'true') return;

      const handle = btn.dataset.productHandle;
      if (!handle) return;

      // initial state
      updateButtonState(btn, list.includes(handle));

      btn.addEventListener('click', () => {
        const updated = toggleWishlist(handle);
        const active = updated.includes(handle);
        updateButtonState(btn, active);

        // if we are on the wishlist page, re-render the grid
        if (
          document.getElementById('sm-wishlist-grid') ||
          document.querySelector('[data-sm-wishlist-list]')
        ) {
          renderWishlistPage();
        }
      });

      btn.dataset.smWishlistInit = 'true';
    });
  }

  // ---------------------------
  // Price formatting – match theme / Shopify format
  // ---------------------------
  function formatPrice(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      // Try to use the same format the theme uses
      const format =
        (Shopify.currency && Shopify.currency.active_format) ||
        Shopify.money_format ||
        undefined;

      try {
        return Shopify.formatMoney(cents, format);
      } catch (e) {
        console.warn('Wishlist price format error', e);
      }
    }

    // Fallback: generic locale currency
    return (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency:
        (window.Shopify &&
          Shopify.currency &&
          Shopify.currency.active) ||
        'EUR'
    });
  }

  // ---------------------------
  // Wishlist page rendering (use product-card layout)
  // ---------------------------
  async function renderWishlistPage() {
    const grid =
      document.getElementById('sm-wishlist-grid') ||
      document.querySelector('[data-sm-wishlist-list]');

    if (!grid) return; // not on wishlist page

    const emptyEl =
      document.getElementById('sm-wishlist-empty') ||
      document.querySelector('[data-sm-wishlist-empty]');

    const noteEl = document.getElementById('sm-wishlist-note');

    const params = new URLSearchParams(window.location.search);
    const sharedItemsParam = params.get('items');
    const isSharedView = !!sharedItemsParam;

    let handles;

    if (isSharedView) {
      // shared view: read from URL, do NOT touch localStorage
      handles = sharedItemsParam.split(',').filter(Boolean);
      if (noteEl) noteEl.textContent = 'You are viewing a shared wishlist.';
    } else {
      // owner view: read from localStorage
      handles = getWishlist();
      if (noteEl) noteEl.textContent = '';
    }

    // update action buttons visibility on page
    setActionsVisibility(handles.length > 0);

    if (!handles.length) {
      if (emptyEl) emptyEl.hidden = false;
      grid.innerHTML = '';
      return;
    } else if (emptyEl) {
      emptyEl.hidden = true;
    }

    // fetch all products in parallel so they appear at once
    const productPromises = handles.map(handle =>
      fetch(`/products/${handle}.js`)
        .then(r => (r.ok ? r.json() : null))
        .catch(() => null)
    );

    const products = await Promise.all(productPromises);

    let html = '';

    products.forEach(product => {
      if (!product) return;

      const mainImage =
        product.images && product.images.length ? product.images[0] : '';
      const url = `/products/${product.handle}`;

      // This markup mimics your collection product-card visually
      html += `
<article class="product-card sm-wishlist-item">
  <div class="product-card__figure">
    <div class="sm-wishlist-card-heart">
      <button
        type="button"
        class="sm-wishlist-btn"
        data-product-handle="${product.handle}"
        aria-pressed="true"
      >
        <svg class="heart-icon heart-icon--empty" viewBox="0 0 24 24">
          <path
            d="M12 21s-7.5-4.5-10-9a6 6 0 0 1 10-6 6 6 0 0 1 10 6c-2.5 4.5-10 9-10 9z"
            fill="none"
            stroke="currentColor"
            stroke-width="1"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <svg class="heart-icon heart-icon--filled" viewBox="0 0 24 24">
          <path
            d="M12 21s-7.5-4.5-10-9a6 6 0 0 1 10-6 6 6 0 0 1 10 6c-2.5 4.5-10 9-10 9z"
            fill="currentColor"
          />
        </svg>
      </button>
    </div>

    <a href="${url}" class="product-card__media" data-instant>
      ${
        mainImage
          ? `<img
               src="${mainImage}"
               alt="${product.title}"
               class="product-card__image product-card__image--primary"
             />`
          : ''
      }
    </a>
  </div>

  <div class="product-card__info empty:hidden">
    <div class="product-card__meta">
      <a href="${url}" class="product-card__title h6">
        ${product.title}
      </a>
    </div>
  </div>
</article>
`;
    });

    grid.innerHTML = html;

    // re-bind hearts inside the newly rendered cards
    initButtons();
  }

  // ---------------------------
  // Share + clear buttons
  // ---------------------------
  function initShareAndClear() {
    const shareBtn =
      document.querySelector('[data-sm-wishlist-share]') ||
      document.getElementById('sm-wishlist-share');

    const clearBtn =
      document.querySelector('[data-sm-wishlist-clear]') ||
      document.getElementById('sm-wishlist-clear');

    const emptyEl =
      document.querySelector('[data-sm-wishlist-empty]') ||
      document.getElementById('sm-wishlist-empty');

    // SHARE: copy single URL with ?items=handle1,handle2,...
    if (shareBtn) {
      shareBtn.addEventListener('click', async () => {
        const items = getWishlist();
        if (!items.length) return;

        const url = new URL(window.location.href);
        url.searchParams.set('items', items.join(','));

        try {
          await navigator.clipboard.writeText(url.toString());
          const oldText = shareBtn.textContent;
          shareBtn.textContent = 'Link copied';
          setTimeout(() => (shareBtn.textContent = oldText), 1500);
        } catch (e) {
          console.error(e);
          alert('Unable to copy automatically. Please copy the URL from the address bar.');
        }
      });
    }

    // CLEAR: wipe storage + UI + hearts
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        saveWishlist([]);
        renderWishlistPage();
        updateWishlistBadge(); // <-- reset badge to 0 / hidden

        if (emptyEl) emptyEl.hidden = false;

        document
          .querySelectorAll('.sm-wishlist-btn.is-active')
          .forEach(btn => updateButtonState(btn, false));
      });
    }
  }

  // ---------------------------
  // Boot
  // ---------------------------
  document.addEventListener('DOMContentLoaded', () => {
    initButtons();
    renderWishlistPage();
    initShareAndClear();
    updateWishlistBadge(); // <-- set initial badge on page load
  });

  document.addEventListener('shopify:section:load', () => {
    initButtons();
    renderWishlistPage();
    // not strictly necessary to call updateWishlistBadge here, header section rarely reloads via sections
  });
})();
